import React, { forwardRef, useImperativeHandle, useState } from 'react';

import { makeStyles } from '@material-ui/core';

import SelectField from './SelectField';

const useStyles = makeStyles(() => ({
  chip: {
    height: '24px',
    margin: '2px 4px 2px 0',
  },
  chipContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '4px',
    maxWidth: '100%',
  },
  field: {
    margin: '8px 0 12px',
  },
  helperText: {
    bottom: -12,
    marginTop: 0,
    position: 'absolute',
    right: 10,
  },
}));

const NewSelectField = forwardRef(
  (
    {
      autoFocus = false,
      className = '',
      disabled = false,
      fullWidth = true,
      items = [],
      label = '',
      margin = 'normal',
      multiple = false,
      name,
      onBlur,
      onChange,
      placeholder = '',
      renderValue,
      required = false,
      size = 'medium',
      value = '',
      variant = 'standard',
    },
    ref
  ) => {
    const classes = useStyles();
    const [internalValue, setInternalValue] = useState(value);
    const [error, setError] = useState('');

    const handleChange = (newValue) => {
      setInternalValue(newValue);
      setError('');

      if (onChange) {
        onChange(newValue);
      }
    };

    const renderMultipleValue = (selected) => {
      if (!selected || selected.length === 0) {
        return <em style={{ color: '#999' }}>{placeholder}</em>;
      }

      if (selected.length === 1) {
        const selectedItem = items.find((item) => item.value === selected[0]);
        return selectedItem ? selectedItem.label : selected[0];
      }

      if (selected.length < items.length) {
        const firstItem = items.find((item) => item.value === selected[0]);
        return `${firstItem ? firstItem.label : selected[0]} + ${selected.length - 1}`;
      }

      return `All ${items.length > 0 ? 'Items' : 'Options'}`;
    };

    const getRenderValue = () => {
      if (renderValue) {
        return renderValue;
      }
      if (multiple) {
        return renderMultipleValue;
      }
      return undefined;
    };

    useImperativeHandle(ref, () => ({
      clearError: () => setError(''),
      getValue: () => internalValue,
      setError: (message) => setError(message),
      setValue: (newValue) => setInternalValue(newValue),
    }));

    return (
      <SelectField
        autoFocus={autoFocus}
        className={`${className} ${classes.field}`}
        disabled={disabled}
        error={!!error}
        fullWidth={fullWidth}
        helperText={error}
        items={items}
        label={label}
        margin={margin}
        multiple={multiple}
        name={name}
        onBlur={onBlur}
        onChange={handleChange}
        placeholder={placeholder}
        renderValue={getRenderValue()}
        required={required}
        size={size}
        value={multiple ? internalValue || [] : internalValue || ''}
        variant={variant}
        FormHelperTextProps={{
          className: classes.helperText,
        }}
      />
    );
  }
);

NewSelectField.displayName = 'NewSelectField';

export default NewSelectField;
